/**
 * Narrow an attempted destination to a path this app could actually have served (HS-1396).
 *
 * Two screens echo back "the page you tried to open" — `Forbidden` from its `?from=` search
 * parameter, and `NotFound` from the unmatched pathname. Both are untrusted: the first is
 * attacker-controllable, the second is whatever the user pasted into the address bar.
 *
 * React escapes the text either way, so this is not about injection. It is about not presenting
 * `//evil.com` — a protocol-relative URL a browser resolves to a DIFFERENT origin — as though it
 * were a page of ours. A screen that says "you tried to open //evil.com" lends that string the
 * app's own credibility, which is the whole trick.
 *
 * Anything that is not a plain same-origin path collapses to `''`, which both callers read as
 * "destination unknown" and render as nothing rather than as an empty label.
 */
export function sameOriginPath(value: unknown): string {
  if (typeof value !== 'string') return '';
  if (!value.startsWith('/')) return '';
  // `//host` and `/\host` are both protocol-relative in browsers; only the first is a valid URL
  // per spec, but the second is accepted in practice, so neither may pass.
  if (value.startsWith('//') || value.startsWith('/\\')) return '';
  return value;
}
